import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { apiFetch } from "../../utils/api";
import { Users as UsersIcon, BarChart3, Sparkles, TrendingUp, ExternalLink, Calendar as CalIcon, Check } from "lucide-react";
import { ClientPickerButton, PeriodPicker, KpiCard, PlatformPill, formatNumber, computePeriodDates } from "./common";
import { SocialDataSkeleton } from "./SocialSkeleton";

export function AnalyticsTab({ clients, selectedClientId, onClientChange }) {
  const clientId = selectedClientId;
  const setClientId = onClientChange;
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  const { from, to } = useMemo(() => computePeriodDates(period), [period]);

  useEffect(() => {
    if (!clientId) { setData(null); setSummary(null); return; }
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ client_id: clientId, sort_by: sortBy, order: "desc", limit: "20", from_date: from, to_date: to });
        if (platform !== "all") qs.set("platform", platform);
        const [detailR, summaryR] = await Promise.all([
          apiFetch(`/zernio/analytics?${qs.toString()}`),
          apiFetch(`/zernio/analytics-summary?client_id=${clientId}&from_date=${from}&to_date=${to}`),
        ]);
        setData(detailR.ok ? await detailR.json() : { posts: [], unavailable: true });
        setSummary(summaryR.ok ? await summaryR.json() : null);
      } finally { setLoading(false); }
    })();
  }, [clientId, platform, sortBy, from, to]);

  const posts = data?.posts || [];
  const overview = data?.overview || {};
  const totalFollowers = summary?.total_followers || 0;
  const followerGrowth = summary?.follower_growth || 0;
  const totalViews = summary?.views || 0;
  const totalImpressions = summary?.impressions || 0;
  const perPlatform = summary?.per_platform || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <ClientPickerButton clients={clients} value={clientId} onChange={setClientId} testid="analytics-client-picker" />
        <PeriodPicker value={period} onChange={setPeriod} testid="analytics-period-picker" />
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-40" data-testid="analytics-platform"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le piattaforme</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="tiktok">TikTok</SelectItem>
            <SelectItem value="youtube">YouTube</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44" data-testid="analytics-sortby"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Data (più recenti)</SelectItem>
            <SelectItem value="engagement">Engagement</SelectItem>
            <SelectItem value="impressions">Impressions</SelectItem>
            <SelectItem value="reach">Reach</SelectItem>
            <SelectItem value="likes">Likes</SelectItem>
            <SelectItem value="views">Views</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">Dati da <strong>Zernio</strong></div>
      </div>

      {!clientId && <Card className="p-8 text-center text-sm text-muted-foreground">Seleziona un cliente per vedere gli analytics.</Card>}
      {clientId && loading && <SocialDataSkeleton />}
      {clientId && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total followers" value={totalFollowers} icon={<UsersIcon className="w-4 h-4" />} testid="kpi-total-followers" />
            <KpiCard
              label={`Follower growth (${period})`}
              value={<span className={followerGrowth >= 0 ? "text-emerald-600" : "text-red-500"}>{followerGrowth >= 0 ? "+" : ""}{formatNumber(followerGrowth)}</span>}
              icon={<TrendingUp className="w-4 h-4" />}
              testid="kpi-follower-growth"
            />
            <KpiCard label={`Views (${period})`} value={totalViews} icon={<BarChart3 className="w-4 h-4" />} testid="kpi-total-views" />
            <KpiCard label={`Impressions (${period})`} value={totalImpressions} icon={<Sparkles className="w-4 h-4" />} testid="kpi-total-impressions" />
          </div>

          {Object.keys(perPlatform).length > 0 && (
            <Card className="overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b text-sm font-semibold">Breakdown per piattaforma</div>
              <div className="grid grid-cols-[1fr_100px_100px_100px_100px] gap-2 px-3 py-2 border-b bg-slate-100/50 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                <div>Piattaforma</div><div className="text-right">Followers</div><div className="text-right">Growth</div><div className="text-right">Views</div><div className="text-right">Post</div>
              </div>
              {Object.entries(perPlatform).map(([p, s]) => (
                <div key={p} className="grid grid-cols-[1fr_100px_100px_100px_100px] gap-2 px-4 py-3 text-xs items-center" data-testid={`platform-row-${p}`}>
                  <div className="capitalize font-medium flex items-center gap-1.5"><PlatformPill platform={p} /> {p}</div>
                  <div className="text-right font-mono">{formatNumber(s.followers)}</div>
                  <div className={`text-right font-mono ${(s.growth || 0) >= 0 ? "text-emerald-600" : "text-red-500"}`}>{(s.growth || 0) >= 0 ? "+" : ""}{formatNumber(s.growth || 0)}</div>
                  <div className="text-right font-mono">{formatNumber(s.views || 0)}</div>
                  <div className="text-right font-mono">{s.posts_count || 0}</div>
                </div>
              ))}
            </Card>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Post totali (all-time)" value={overview.totalPosts || 0} icon={<BarChart3 className="w-4 h-4" />} />
            <KpiCard label="Pubblicati" value={overview.publishedPosts || 0} icon={<Check className="w-4 h-4" />} />
            <KpiCard label="Programmati" value={overview.scheduledPosts || 0} icon={<CalIcon className="w-4 h-4" />} />
          </div>

          {data?.unavailable ? (
            <Card className="p-4 border-amber-200 bg-amber-50">
              <div className="text-sm text-amber-800 font-medium">Dettagli analytics non disponibili</div>
              <div className="text-xs text-amber-700 mt-1">{data.reason || "L'endpoint analytics richiede un add-on premium su questo profilo."}</div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b text-sm font-semibold">Post nel periodo selezionato</div>
              <div className="grid grid-cols-[64px_1fr_100px_100px_100px_80px] gap-2 px-3 py-2 border-b bg-slate-100/50 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                <div>Post</div><div>Contenuto</div><div className="text-right">Impressions</div><div className="text-right">Reach</div><div className="text-right">Eng. rate</div><div className="text-right">Link</div>
              </div>
              {posts.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Nessun post nel periodo</div>
              ) : posts.map((p, i) => {
                const a = p.analytics || {};
                return (
                  <div key={p.postId || p._id || i} className="grid grid-cols-[64px_1fr_100px_100px_100px_80px] gap-3 px-4 py-3 text-xs items-center" data-testid={`analytics-row-${i}`}>
                    <div>{p.thumbnailUrl ? <img src={p.thumbnailUrl} alt="" className="w-12 h-12 rounded object-cover" /> : <div className="w-12 h-12 bg-slate-100 rounded" />}</div>
                    <div className="truncate">
                      <div className="truncate font-medium">{p.content?.slice(0, 80)}</div>
                      <div className="text-[10px] text-slate-500">{p.publishedAt ? new Date(p.publishedAt).toLocaleString("it-IT") : "—"} · {p.platform || p.platformAnalytics?.[0]?.platform}</div>
                    </div>
                    <div className="text-right font-mono">{formatNumber(a.impressions || 0)}</div>
                    <div className="text-right font-mono">{formatNumber(a.reach || 0)}</div>
                    <div className="text-right font-mono">{(a.engagementRate || 0).toFixed(2)}%</div>
                    <div className="text-right">{p.platformPostUrl && <a href={p.platformPostUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline"><ExternalLink className="w-3.5 h-3.5 inline" /></a>}</div>
                  </div>
                );
              })}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
